import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { FormCard, SelectableCard } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  CLAIM_CALL_ASSISTANCE_REASON_OFF_ROAD,
} from '../claims/claimCallAssistanceLocationState'
import {
  buildClaimIncidentHref,
  CLAIM_FLOW_PATH,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  formatSingleVehicleCollisionRailSummary,
} from '../claims/claimFlowModel'
import {
  OTHER_CLAIM_SUBTYPE_OPTIONS,
  type OtherClaimSubtypeId,
} from '../claims/otherClaimSubtypeOptions'
import { persistOtherClaimSubtype, readClaimDraft } from '../claims/claimDraftStorage'
import { readPolicyFromListCache } from '../utils/policiesListCache'
import { getPolicyType, type ClaimablePolicyType } from '../utils/policyType'

function isClaimablePolicyType(value: string | null): value is ClaimablePolicyType {
  return value === '4x4' || value === 'caravan' || value === 'camper' || value === 'unsupported'
}

/** Restores the progress-rail highlight from a persisted draft, if any. */
function highlightFromDraft(): OtherClaimSubtypeId | null {
  const fromDraft = readClaimDraft()?.otherClaimSubtypeId
  return OTHER_CLAIM_SUBTYPE_OPTIONS.some((o) => o.id === fromDraft)
    ? (fromDraft as OtherClaimSubtypeId)
    : null
}

/**
 * Non–windscreen funnel: user picks a specific claim category (collision, theft, etc.) before when & where.
 *
 * Safe-to-drive is confirmed on PreClaim before this screen. `off-road` goes straight to phone triage.
 */
export default function ClaimOtherSubtypeStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const policyRefFromQuery = decodePolicyRef(queryParams.get('policyRef'))
  const typeFromQueryRaw = queryParams.get('type')
  const typeFromQuery: ClaimablePolicyType | null = isClaimablePolicyType(typeFromQueryRaw)
    ? typeFromQueryRaw
    : null
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null
  const fromListCache = readPolicyFromListCache(policyRefFromQuery)
  const bootstrapPolicy = navState?.policy ?? fromListCache
  const policy = bootstrapPolicy ?? null
  const targetPolicyRef = policy?.policyRef || policyRefFromQuery
  const policyType = typeFromQuery ?? getPolicyType(policy)
  const policySummary = formatClaimPolicySummaryLine(policy ?? (targetPolicyRef ? { policyRef: targetPolicyRef } : null))

  const [highlightId, setHighlightId] = useState<OtherClaimSubtypeId | null>(() => highlightFromDraft())

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const typeHint =
    policyType === '4x4' || policyType === 'caravan' || policyType === 'camper' ? policyType : null

  const handleBack = () => {
    const p = new URLSearchParams()
    if (targetPolicyRef) p.set('policyRef', targetPolicyRef)
    if (typeHint) p.set('type', typeHint)
    const q = p.toString()
    const path = q ? `${CLAIM_FLOW_PATH.selectType}?${q}` : CLAIM_FLOW_PATH.selectType
    navigate(path, { state: policy ? { policy } : undefined })
  }

  /** Navigates to the off-road / phone-only path without persisting an online FNOL draft. */
  const navigateOffRoadCallAssistance = () => {
    navigate('/claims/call-for-assistance', {
      state: {
        assistanceReason: CLAIM_CALL_ASSISTANCE_REASON_OFF_ROAD,
        policy: policy ?? undefined,
        policyRef: targetPolicyRef ?? null,
        typeHint,
      },
    })
  }

  const chooseSubtype = (id: OtherClaimSubtypeId) => {
    setHighlightId(id)
    if (id === 'off-road') {
      navigateOffRoadCallAssistance()
      return
    }
    persistOtherClaimSubtype(targetPolicyRef ?? null, id)
    navigate(buildClaimIncidentHref(targetPolicyRef ?? null, typeHint, 'other', id), {
      state: policy ? { policy } : undefined,
    })
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader
        backAriaLabel="Back"
        onBack={handleBack}
        title="Tell us what happened"
      />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={highlightFromDraft()}
            incidentSummary={formatIncidentRailSummary(readClaimDraft())}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(readClaimDraft())}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(readClaimDraft())}
            multiVehicleIncidentDescription={readClaimDraft()?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={readClaimDraft()?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={readClaimDraft()?.multiVehicleClaimDescription ?? null}
            weatherClaimConditionsDescription={readClaimDraft()?.weatherClaimConditionsDescription ?? null}
            fireClaimTypeDescription={readClaimDraft()?.fireClaimTypeDescription ?? null}
            theftOrBreakInDetailDescription={readClaimDraft()?.theftOrBreakInDetailDescription ?? null}
            singleVehicleCollisionSummary={formatSingleVehicleCollisionRailSummary(readClaimDraft())}
            windscreenGstRegistered={readClaimDraft()?.windscreenGstRegistered ?? null}
            windscreenAbn={readClaimDraft()?.windscreenAbn ?? null}
            windscreenItcEntitled={readClaimDraft()?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={readClaimDraft()?.windscreenItcPercentage ?? null}
          />
        }
      >
        <FormCard>
          <div className="flex flex-col gap-[20px] items-start w-full">
            <h2
              id="other-claim-type-heading"
              className="font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              What type of claim would you like to make?
            </h2>

            <ul
              aria-labelledby="other-claim-type-heading"
              className="flex flex-col gap-[12px] md:gap-[16px] w-full list-none p-0 m-0"
            >
              {OTHER_CLAIM_SUBTYPE_OPTIONS.map((option) => (
                <li key={option.id} className="w-full">
                  <SelectableCard
                    prominent
                    selected={highlightId === option.id}
                    headline={option.headline}
                    caption={option.caption}
                    onClick={() => chooseSubtype(option.id)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </FormCard>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
